import { useEffect, useRef, useState } from 'react'
import { Modal, Tooltip, Typography, Spin } from 'antd'
import { HelpCircle } from 'lucide-react'
import styled from 'styled-components'
import { getConfidenceColor, getConfidenceStatus, CONFIDENCE_LABELS } from './confidenceUtils'
import type { ConfidenceScoreEntry } from './confidenceScoresStore'
import { setScores } from './confidenceScoresStore'
import { fetchConfidenceEvidence } from '../../services/api/confidenceEvidenceApi'
import type { ConfidenceEvidencePayload } from '../../services/api/confidenceEvidenceApi'
import { fetchConfidenceScores } from '../../services/api/confidenceScoreApi'

const { Text, Title } = Typography

// ── Styled components ─────────────────────────────────────────────────────────

const Header = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--color-border);
  margin-bottom: 20px;
`

const ScorePill = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: ${({ $color }) => $color}18;
  border: 2px solid ${({ $color }) => $color};
  font-size: 18px;
  font-weight: 700;
  color: ${({ $color }) => $color};
  flex-shrink: 0;
`

const StatusBadge = styled.span<{ $color: string }>`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $color }) => $color}20;
  color: ${({ $color }) => $color};
  margin-top: 2px;
`

const Section = styled.div`
  margin-bottom: 20px;
`

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--color-text-muted);
  margin-bottom: 10px;
  display: inline-flex;
  align-items: center;
`

const DimensionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
`

const DimensionLabel = styled.span`
  font-size: 13px;
  color: var(--color-text-tertiary);
  width: 110px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
`

const DimensionBar = styled.div`
  flex: 1;
  height: 6px;
  background: var(--color-bg-hover);
  border-radius: 3px;
  overflow: hidden;
`

const DimensionFill = styled.div<{ $pct: number; $color: string }>`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  background: ${({ $color }) => $color};
  border-radius: 3px;
  transition: width 0.4s ease;
`

const DimensionValue = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-secondary);
  width: 32px;
  text-align: right;
  flex-shrink: 0;
`

const FormulaBox = styled.div`
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border-card);
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  color: var(--color-text-secondary);
  font-family: 'SF Mono', 'Fira Code', monospace;
  line-height: 1.6;
`

const ReasoningText = styled.div`
  font-size: 14px;
  color: var(--color-text-secondary);
  line-height: 1.6;
  background: var(--color-bg-secondary);
  border-radius: 8px;
  padding: 12px 14px;
`

const EvidenceList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`

type EvidenceColorVariant = 'survey' | 'interview' | 'document'

const EVIDENCE_COLORS: Record<EvidenceColorVariant, string> = {
  survey: '#6366f1',
  interview: '#10b981',
  document: '#f59e0b',
}

const EvidenceItem = styled.li<{ $type: EvidenceColorVariant }>`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--color-text-secondary);
  padding: 6px 10px;
  background: var(--color-bg-secondary);
  border-radius: 6px;
  border-left: 3px solid ${({ $type }) => EVIDENCE_COLORS[$type]};
`

const EvidenceTypeTag = styled.span<{ $type: EvidenceColorVariant }>`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  color: ${({ $type }) => EVIDENCE_COLORS[$type]};
  flex-shrink: 0;
  width: 60px;
`

const EmptyEvidence = styled.div`
  font-size: 13px;
  color: var(--color-text-muted);
  font-style: italic;
`

// ── Tooltip copy (academic methodology, kept terse) ───────────────────────────

const TOOLTIPS = {
  ulwick:
    'Ulwick Opportunity Score (Jobs-to-be-Done). Strong opportunities are important needs that current solutions handle poorly. Formula: Importance + max(Importance − Satisfaction, 0), scaled 0–100.',
  ice: 'ICE scoring (Impact, Confidence, Effort) for early-stage validation. Per Savoia\'s Pretotyping, weigh real "skin in the game" evidence over opinions. Formula: (Impact + Viability + Low Friction) ÷ 3 × 10.',
  importance:
    'How frequently and urgently users express this need — mention frequency, pain severity, willingness to pay.',
  satisfaction:
    'How well current solutions address this need. Low = no good solution exists. High = existing solutions already work.',
  impact:
    'How directly this idea addresses an expressed unmet need. Strong impact = users would find it highly desirable based on what they actually said.',
  viability:
    '"Skin in the game" evidence — money spent, time committed, behaviors shown. Per Savoia, opinions are weak signals; payments are strong ones.',
  lowFriction:
    "How easy adoption is from the user's side — NOT how hard to build. Walk-in vs advance booking, one-tap vs multi-step.",
  reasoning:
    "The AI's rationale for these scores, citing facts or quotes from the linked research.",
  evidence:
    'Research that contributed to this score. New research targeting this node will trigger a re-score.',
} as const

// Render the tooltip inside the modal so it inherits the modal's stacking
// context and is guaranteed to sit above the modal content. Reset the
// inherited typography (SectionLabel applies text-transform: uppercase and
// bold) on the tooltip body so the help text reads normally.
const HelpHint = ({ text }: { text: string }) => (
  <Tooltip
    title={text}
    mouseEnterDelay={0.2}
    placement="top"
    getPopupContainer={(triggerNode) => triggerNode.parentElement ?? document.body}
    styles={{
      root: { maxWidth: 280 },
      body: {
        textTransform: 'none',
        fontWeight: 400,
        letterSpacing: 'normal',
        fontSize: 13,
        lineHeight: 1.5,
      },
    }}
  >
    <HelpCircle
      size={12}
      style={{
        color: 'var(--color-text-tertiary)',
        cursor: 'default',
        flexShrink: 0,
        marginLeft: 4,
        verticalAlign: 'middle',
      }}
    />
  </Tooltip>
)

// ── Types ─────────────────────────────────────────────────────────────────────

interface ConfidenceDetailModalProps {
  open: boolean
  onClose: () => void
  nodeLabel: string
  mindmapId: string
  nodeId: string
  scoreEntry: ConfidenceScoreEntry
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function NeedFormulaBreakdown({ dim, score }: { dim: Record<string, number>; score: number }) {
  const importance = dim.importance ?? 0
  const satisfaction = dim.satisfaction ?? 0
  const raw = importance + Math.max(importance - satisfaction, 0)
  const color = getConfidenceColor(score)

  return (
    <>
      <DimensionRow>
        <DimensionLabel>
          Importance
          <HelpHint text={TOOLTIPS.importance} />
        </DimensionLabel>
        <DimensionBar>
          <DimensionFill $pct={importance * 10} $color={color} />
        </DimensionBar>
        <DimensionValue>{importance}/10</DimensionValue>
      </DimensionRow>
      <DimensionRow>
        <DimensionLabel>
          Satisfaction
          <HelpHint text={TOOLTIPS.satisfaction} />
        </DimensionLabel>
        <DimensionBar>
          <DimensionFill $pct={satisfaction * 10} $color="#9ca3af" />
        </DimensionBar>
        <DimensionValue>{satisfaction}/10</DimensionValue>
      </DimensionRow>
      <FormulaBox style={{ marginTop: 10 }}>
        {importance} + max({importance} − {satisfaction}, 0) = {raw}/20 → {score}/100
      </FormulaBox>
    </>
  )
}

function IdeaFormulaBreakdown({ dim, score }: { dim: Record<string, number>; score: number }) {
  const impact = dim.impact ?? 0
  const viability = dim.viability ?? 0
  const lowFriction = dim.lowFriction ?? 0
  const color = getConfidenceColor(score)

  return (
    <>
      <DimensionRow>
        <DimensionLabel>
          Impact
          <HelpHint text={TOOLTIPS.impact} />
        </DimensionLabel>
        <DimensionBar>
          <DimensionFill $pct={impact * 10} $color={color} />
        </DimensionBar>
        <DimensionValue>{impact}/10</DimensionValue>
      </DimensionRow>
      <DimensionRow>
        <DimensionLabel>
          Viability
          <HelpHint text={TOOLTIPS.viability} />
        </DimensionLabel>
        <DimensionBar>
          <DimensionFill $pct={viability * 10} $color={color} />
        </DimensionBar>
        <DimensionValue>{viability}/10</DimensionValue>
      </DimensionRow>
      <DimensionRow>
        <DimensionLabel>
          Low Friction
          <HelpHint text={TOOLTIPS.lowFriction} />
        </DimensionLabel>
        <DimensionBar>
          <DimensionFill $pct={lowFriction * 10} $color={color} />
        </DimensionBar>
        <DimensionValue>{lowFriction}/10</DimensionValue>
      </DimensionRow>
      <FormulaBox style={{ marginTop: 10 }}>
        ({impact} + {viability} + {lowFriction}) ÷ 3 × 10 = {score}/100
      </FormulaBox>
    </>
  )
}

// ── Modal ─────────────────────────────────────────────────────────────────────

export function ConfidenceDetailModal({
  open,
  onClose,
  nodeLabel,
  mindmapId,
  nodeId,
  scoreEntry,
}: ConfidenceDetailModalProps) {
  const [evidence, setEvidence] = useState<ConfidenceEvidencePayload | null>(null)
  const [evidenceLoading, setEvidenceLoading] = useState(false)
  const refreshRunRef = useRef(0)

  // Render directly from the prop — it tracks the in-memory store, which is
  // kept fresh by the score_updated realtime broadcast. Any local shadow state
  // would freeze at the moment the modal opened and miss subsequent rollups.
  const color = getConfidenceColor(scoreEntry.score)
  const status = getConfidenceStatus(scoreEntry.score)
  const isNeed = scoreEntry.nodeCategory === 'need'

  // Refresh on open AND whenever the rollup recomputes (computedAt changes).
  // - open=true / nodeId change → first fetch
  // - scoreEntry.computedAt change → a new rollup just landed; evidence list
  //   may have grown (new survey close, new interview note); re-fetch.
  // The parallel fetchConfidenceScores call is belt-and-suspenders against a
  // missed broadcast on modal open — it writes back into the store so the
  // badge and any other consumers also stay in sync.
  useEffect(() => {
    if (!open) return

    const runId = ++refreshRunRef.current
    setEvidenceLoading(true)
    void Promise.all([
      fetchConfidenceEvidence(mindmapId, nodeId),
      fetchConfidenceScores(mindmapId),
    ]).then(([evidenceData, allScores]) => {
      if (refreshRunRef.current !== runId) return

      setEvidence(evidenceData)
      setEvidenceLoading(false)

      if (allScores) setScores(mindmapId, allScores)
    })
  }, [open, mindmapId, nodeId, scoreEntry.computedAt])

  const totalSources =
    (evidence?.surveys.length ?? 0) +
    (evidence?.interviewNotes.length ?? 0) +
    (evidence?.documents.length ?? 0)

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={480}
      title={null}
      styles={{ body: { padding: '24px' } }}
    >
      <Header $color={color}>
        <ScorePill $color={color}>{scoreEntry.score}</ScorePill>
        <div>
          <Title level={5} style={{ margin: 0, lineHeight: 1.3 }}>
            {nodeLabel}
          </Title>
          <StatusBadge $color={color}>{CONFIDENCE_LABELS[status]}</StatusBadge>
        </div>
      </Header>

      {scoreEntry.dimensions && (
        <Section>
          <SectionLabel>
            {isNeed ? 'Ulwick Opportunity Score' : 'ICE Score Breakdown'}
            <HelpHint text={isNeed ? TOOLTIPS.ulwick : TOOLTIPS.ice} />
          </SectionLabel>
          {isNeed ? (
            <NeedFormulaBreakdown dim={scoreEntry.dimensions} score={scoreEntry.score} />
          ) : (
            <IdeaFormulaBreakdown dim={scoreEntry.dimensions} score={scoreEntry.score} />
          )}
        </Section>
      )}

      {scoreEntry.reasoning && (
        <Section>
          <SectionLabel>
            Why this score
            <HelpHint text={TOOLTIPS.reasoning} />
          </SectionLabel>
          <ReasoningText>{scoreEntry.reasoning}</ReasoningText>
        </Section>
      )}

      <Section style={{ marginBottom: 0 }}>
        <SectionLabel>
          Evidence sources{!evidenceLoading && evidence ? ` (${totalSources})` : ''}
          <HelpHint text={TOOLTIPS.evidence} />
        </SectionLabel>
        {evidenceLoading ? (
          <Spin size="small" />
        ) : !evidence || totalSources === 0 ? (
          <EmptyEvidence>
            No closed surveys, completed interviews, or uploaded research found.
          </EmptyEvidence>
        ) : (
          <EvidenceList>
            {evidence.surveys.map((s) => (
              <EvidenceItem key={s.id} $type="survey">
                <EvidenceTypeTag $type="survey">Survey</EvidenceTypeTag>
                <Text ellipsis style={{ fontSize: 13 }}>
                  {s.label || 'Untitled survey'}
                </Text>
              </EvidenceItem>
            ))}
            {evidence.interviewNotes.map((n) => (
              <EvidenceItem key={n.noteId} $type="interview">
                <EvidenceTypeTag $type="interview">Interview</EvidenceTypeTag>
                <Text ellipsis style={{ fontSize: 13 }}>
                  {n.intervieweeName ? `${n.intervieweeName} — ` : ''}
                  {n.interviewLabel}
                </Text>
              </EvidenceItem>
            ))}
            {evidence.documents.map((d) => (
              <EvidenceItem key={d.id} $type="document">
                <EvidenceTypeTag $type="document">
                  {d.type === 'survey' ? 'Doc · Survey' : 'Doc · Interview'}
                </EvidenceTypeTag>
                <Text ellipsis style={{ fontSize: 13 }}>
                  {d.fileName}
                </Text>
              </EvidenceItem>
            ))}
          </EvidenceList>
        )}
      </Section>
    </Modal>
  )
}
